import { useState } from "react";
import { toast } from "sonner";
import { Flame, Snowflake, StickyNote, Sun, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import CloseReasonDialog from "@/components/CloseReasonDialog";

const BUILTIN_STAGES = {
  mbbs: ["New", "Contacted", "Follow-up", "Zoom Done", "Visited", "Docs Recd", "Enrolled", "Closed-lost", "Wrong number"],
  general: ["Untouched", "Not Connected", "Cold", "Warm", "Future Lead", "Closed", "Referred"],
  counseling: ["New", "Docs Received", "Visited", "Zoom Done", "Options Sent", "Enrolled", "Spoke", "Future Lead", "Closed"],
};
const CLOSE_VALUES = new Set(["Closed", "Closed-lost"]);
const TEMPS = [
  { key: "Hot", icon: Flame, cls: "bg-rose-600 text-white", idle: "text-rose-500 hover:bg-rose-50" },
  { key: "Warm", icon: Sun, cls: "bg-amber-500 text-white", idle: "text-amber-500 hover:bg-amber-50" },
  { key: "Cold", icon: Snowflake, cls: "bg-sky-600 text-white", idle: "text-sky-500 hover:bg-sky-50" },
];

export default function InboxStatusBar({ convo, customStages = {}, onChanged }) {
  const counseling = convo.counseling_active;
  const field = counseling ? "counseling_status" : "status";
  const current = counseling ? convo.counseling_status : convo.status;
  const stages = counseling
    ? BUILTIN_STAGES.counseling
    : BUILTIN_STAGES[convo.pipeline] || customStages[convo.pipeline] || BUILTIN_STAGES.general;

  const [saving, setSaving] = useState(false);
  const [temp, setTemp] = useState(convo.temperature || null);
  const [pendingClose, setPendingClose] = useState(null);
  const [note, setNote] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);

  const patch = async (body) => {
    setSaving(true);
    try {
      await api.patch(`/admin/leads/${convo.lead_id}`, body);
      toast.success("Updated");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onStatus = (val) => {
    if (val === current) return;
    if (CLOSE_VALUES.has(val)) {
      setPendingClose(val);
      return;
    }
    patch({ [field]: val });
  };

  const confirmClose = ({ reason, detail }) => {
    const val = pendingClose;
    setPendingClose(null);
    patch({ [field]: val, close_reason: reason, close_reason_detail: detail });
  };

  const setTemperature = (key) => {
    setTemp(key);
    patch({ lead_temperature: key });
  };

  const saveNote = async () => {
    if (!note.trim()) return;
    setNoteSaving(true);
    try {
      await api.post(`/admin/leads/${convo.lead_id}/notes`, { content: note.trim() });
      toast.success("Note added");
      setNote("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNoteSaving(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="inbox-status-bar">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={current || ""} onValueChange={onStatus} disabled={saving}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="inbox-status-select">
            <SelectValue placeholder="Set stage" />
          </SelectTrigger>
          <SelectContent>
            {stages.map((s) => (
              <SelectItem key={s} value={s} data-testid={`inbox-stage-${s}`}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1">
          {TEMPS.map(({ key, icon: Icon, cls, idle }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTemperature(key)}
              className={`flex h-8 items-center gap-1 rounded-md px-2 text-xs font-medium transition-colors ${temp === key ? cls : idle}`}
              data-testid={`inbox-temp-${key}`}
              title={`Mark ${key}`}
            >
              <Icon className="h-3.5 w-3.5" /> {key}
            </button>
          ))}
        </div>
        {saving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>

      <div className="flex items-center gap-2">
        <StickyNote className="h-4 w-4 shrink-0 text-slate-400" />
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && saveNote()}
          placeholder="Quick note (e.g. will decide after results)…"
          className="h-8 text-xs"
          data-testid="inbox-quick-note"
        />
        <button
          type="button"
          onClick={saveNote}
          disabled={noteSaving || !note.trim()}
          className="rounded-md bg-[#0A2540] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#1E3A5F] disabled:opacity-40"
          data-testid="inbox-quick-note-save"
        >
          {noteSaving ? "…" : "Add"}
        </button>
      </div>

      <CloseReasonDialog
        open={!!pendingClose}
        onOpenChange={(v) => !v && setPendingClose(null)}
        onConfirm={confirmClose}
      />
    </div>
  );
}
